import React, { useEffect, useState } from "react";
import { isNativeApp } from "../mobile/native";

/**
 * Dev-only pill: which git branch the local folder is on. A git post-checkout hook writes
 * `emofront/public/dev-branch.txt` (git-ignored); this reads it and shows it bottom-left, only on
 * localhost / LAN dev hosts. In production the file doesn't exist and the host check fails, so
 * it never renders. (Isaac: "maybe I need a flag on the site to remind me".)
 */
const isDevHost = (): boolean => {
  // Belt and braces: a PRODUCTION bundle never shows it, wherever it is served from. CRA sets
  // NODE_ENV=production for `npm run build`, so this covers Render, a phone app, and any local
  // build someone deploys by hand — dev-branch.txt is gitignored but CRA copies public/ verbatim.
  if (process.env.NODE_ENV === "production") return false;
  // The Capacitor shell serves the bundled app from https://localhost, so the host check alone
  // is true inside a SHIPPED phone app — and `dev-branch.txt` rides along in the bundle. Without
  // this guard every APK shows the branch the Mac happened to be on, over the app's own UI.
  if (isNativeApp()) return false;
  const h = window.location.hostname;
  return h === "localhost" || h === "127.0.0.1" || /^192\.168\./.test(h) || /^10\./.test(h);
};

const readBranch = async (): Promise<string> => {
  try {
    const r = await fetch(`/dev-branch.txt?t=${Date.now()}`, { cache: "no-store" });
    if (!r.ok) return "";
    const t = (await r.text()).trim();
    // The dev server answers index.html for a missing file — that is not a branch name.
    if (!t || t.startsWith("<") || t.length > 80 || /\s/.test(t)) return "";
    return t;
  } catch (e) {
    return "";
  }
};

const DevBranchBadge: React.FC = () => {
  const [branch, setBranch] = useState<string>("");
  useEffect(() => {
    if (!isDevHost()) return undefined;
    let cancelled = false;
    const tick = () => { readBranch().then((b) => { if (!cancelled) setBranch(b); }); };
    tick();
    const id = setInterval(tick, 15000); // a branch switch shows up without a reload
    return () => { cancelled = true; clearInterval(id); };
  }, []);
  if (!branch) return null;
  return (
    <div
      title="The git branch your local folder is on (dev only, never shown in production)"
      style={{ position: "fixed", bottom: 8, left: 8, zIndex: 12500, fontFamily: "Outfit, system-ui, sans-serif", fontSize: 11, fontWeight: 700, color: "#fff", background: "rgba(20,20,28,0.92)", border: "1px solid rgba(233,30,79,0.6)", borderRadius: 999, padding: "4px 10px", pointerEvents: "none", opacity: 0.85, whiteSpace: "nowrap" }}
    >
      🌿 {branch}
    </div>
  );
};

export default DevBranchBadge;
